// Saved tunes, guarded at the trust boundary.
//
// `savedCharts` comes back from localStorage on every boot, and until now it
// was taken on faith: only the one-time v1→v4 lift ever inspected it, so a
// blob corrupted afterwards — a truncated write, a half-finished quota
// failure, a hand-edited devtools value, a bug in a future writer — reached
// the Tunes browser and the ⌘K palette as a `Score` that isn't one. The
// failure mode is a render crash on a list the user can't get past, with
// their other saved tunes trapped behind it.
//
// So: validate on the way in, keep what is sound, drop what is not, and say
// how many were dropped. Losing one corrupt entry is recoverable; losing the
// whole list to an exception is not.
//
// NB this deliberately does NOT route saved tunes through `DocumentStore`.
// That interface exists for revisions, optimistic-concurrency conflicts and
// swappable async backends — none of which browser-local saved tunes have or
// need, and forcing them through would push loading states into two
// synchronous React surfaces for no benefit. What they were missing was
// validation, which is shared with the catalogue path via `validateDocument`.

import { normalizeScore } from '@/music/score/normalize';
import type { Score } from '@/music/score/types';
import {
  isScoreShaped,
  MALFORMED_SCORE,
  SCHEMA_VERSION,
  validateDocument,
  type ScoreDoc,
} from './documents';

/** Wrap a saved Score as a document so one validator serves both paths. */
export function scoreToDoc(score: Score): ScoreDoc {
  return {
    kind: 'score',
    id: score.id ?? '',
    schemaVersion: SCHEMA_VERSION,
    name: score.title,
    score,
  };
}

export interface SavedTunesCheck {
  /** The entries that are sound, in their original order. */
  kept: Score[];
  /** One line per dropped entry, for a console warning — never silent. */
  dropped: string[];
}

/**
 * Filter a persisted saved-tune list to the entries that are actually usable.
 *
 * A saved tune must have: an object shape, a title the UI can render, and a
 * stable id — the id is what per-tune band memory and playlist references key
 * on, so an entry without one is unreachable anyway. Structural validation of
 * the document wrapper is shared with the catalogue via `validateDocument`.
 */
export function checkSavedTunes(raw: unknown): SavedTunesCheck {
  if (!Array.isArray(raw)) {
    return { kept: [], dropped: raw == null ? [] : ['savedCharts was not an array'] };
  }
  const kept: Score[] = [];
  const dropped: string[] = [];
  const seen = new Set<string>();

  raw.forEach((entry, i) => {
    const at = `savedCharts[${i}]`;
    if (!entry || typeof entry !== 'object') {
      dropped.push(`${at}: not an object`);
      return;
    }
    const score = entry as Score;
    if (typeof score.title !== 'string' || !score.title.trim()) {
      dropped.push(`${at}: missing title`);
      return;
    }
    if (typeof score.id !== 'string' || !score.id.trim()) {
      // Without an id the tune can't be deleted, referenced by a playlist, or
      // carry band memory — it is already broken, just not visibly.
      dropped.push(`${at} (${score.title}): missing stable id`);
      return;
    }
    if (seen.has(score.id)) {
      // Duplicate ids make delete-by-id ambiguous; keep the first.
      dropped.push(`${at} (${score.title}): duplicate id ${score.id}`);
      return;
    }
    // NORMALIZE FIRST, then validate. `normalizeScore` repairs the repairable
    // (a part short of the form's measure count, a missing final barline), so
    // validation drops only what is genuinely unreadable. Validating the raw
    // blob would discard tunes the model can fix by itself — and losing a
    // user's tune to a strict check is worse than the crash this guards.
    // `score` is only Score-typed by assertion; the guard is what makes that
    // true, so its title is read before narrowing rules the branch out.
    const title = score.title;
    if (!isScoreShaped(score)) {
      dropped.push(`${at} (${title}): ${MALFORMED_SCORE}`);
      return;
    }
    let normalized: Score;
    try {
      normalized = normalizeScore(score);
    } catch (error) {
      dropped.push(
        `${at} (${score.title}): ${error instanceof Error ? error.message : 'could not be read'}`,
      );
      return;
    }
    const issues = validateDocument(scoreToDoc(normalized));
    // The id pattern is a REPOSITORY filename constraint; browser ids come
    // from uid()/openbook and need not satisfy it, so that one is not fatal
    // here. Everything else is.
    const fatal = issues.filter((issue) => issue.path !== 'id');
    if (fatal.length) {
      dropped.push(`${at} (${score.title}): ${fatal.map((f) => f.message).join('; ')}`);
      return;
    }
    seen.add(score.id);
    kept.push(normalized);
  });

  return { kept, dropped };
}
